import type { MusicProvider, Track, PlayerState } from "@/types/music-provider";

declare global {
  interface Window {
    DZ: any;
  }
}

export class DeezerProvider implements MusicProvider {
  type = 'deezer' as const;
  name = 'Deezer';
  isAuthenticated = false;
  isReady = false;
  
  private playerState: PlayerState = {
    isPlaying: false,
    currentTrack: null,
    position: 0,
    duration: 0,
    volume: 100
  };

  async login(): Promise<void> {
    await this.initializeDeezer();
    
    return new Promise((resolve, reject) => {
      window.DZ.login((response: any) => {
        if (response.authResponse) {
          this.isAuthenticated = true;
          resolve();
        } else {
          reject(new Error('Login cancelado'));
        }
      }, { perms: 'basic_access,email,listening_history' });
    });
  }

  async logout(): Promise<void> {
    if (window.DZ) {
      window.DZ.logout();
      this.isAuthenticated = false;
      this.isReady = false;
    }
  }

  async checkAuthStatus(): Promise<boolean> {
    await this.initializeDeezer();
    
    return new Promise((resolve) => {
      window.DZ.getLoginStatus((response: any) => {
        this.isAuthenticated = response.status === 'connected';
        resolve(this.isAuthenticated);
      });
    });
  }

  async play(trackId?: string): Promise<void> {
    if (!window.DZ) throw new Error('Deezer não inicializado');
    
    if (trackId) {
      window.DZ.player.playTracks([trackId]);
    } else {
      window.DZ.player.play();
    }
    this.playerState.isPlaying = true;
  }

  async pause(): Promise<void> {
    if (window.DZ) {
      window.DZ.player.pause();
      this.playerState.isPlaying = false;
    }
  }

  async resume(): Promise<void> {
    await this.play();
  }

  async next(): Promise<void> {
    if (window.DZ) {
      window.DZ.player.next();
    }
  }

  async previous(): Promise<void> {
    if (window.DZ) {
      window.DZ.player.prev();
    }
  }

  async seek(positionMs: number): Promise<void> {
    if (window.DZ) {
      window.DZ.player.seek((positionMs / 1000) / window.DZ.player.getDuration() * 100);
    }
  }

  async setVolume(volume: number): Promise<void> {
    if (window.DZ) {
      window.DZ.player.setVolume(volume);
      this.playerState.volume = volume;
    }
  }

  getPlayerState(): PlayerState {
    return this.playerState;
  }

  getCurrentTrack(): Track | null {
    if (!window.DZ) return null;
    
    const track = window.DZ.player.getCurrentTrack();
    if (!track) return null;
    
    return {
      id: track.id,
      name: track.title,
      artist: track.artist.name,
      album: track.album.title,
      album_art: track.album.cover_medium,
      duration_ms: track.duration * 1000,
      external_url: track.link
    };
  }

  async searchTracks(query: string, limit = 20): Promise<Track[]> {
    if (!window.DZ) return [];
    
    return new Promise((resolve) => {
      window.DZ.api(`/search/track?q=${encodeURIComponent(query)}&limit=${limit}`, (response: any) => {
        const tracks = response.data.map((track: any) => ({
          id: track.id.toString(),
          name: track.title,
          artist: track.artist.name,
          album: track.album.title,
          bpm: track.bpm,
          album_art: track.album.cover_medium,
          duration_ms: track.duration * 1000,
          external_url: track.link
        }));
        resolve(tracks);
      });
    });
  }

  async getRecommendations(seedTracks: string[], targetBPM?: number): Promise<Track[]> {
    // Deezer não tem API pública de recomendações baseadas em BPM
    // Retorna busca genérica
    return this.searchTracks('workout running', 50);
  }

  private async initializeDeezer(): Promise<void> {
    if (this.isReady) return;

    // Carregar Deezer SDK
    if (!window.DZ) {
      const script = document.createElement('script');
      script.src = 'https://e-cdns-files.dzcdn.net/js/min/dz.js';
      script.async = true;
      document.head.appendChild(script);
      
      await new Promise((resolve) => {
        script.onload = resolve;
      });
    }

    // Inicializar (requer app ID - deve ser configurado pelo usuário)
    window.DZ.init({
      appId: 'YOUR_DEEZER_APP_ID',
      channelUrl: `${window.location.origin}/deezer-channel.html`,
      player: {
        onload: () => {
          this.isReady = true;
        }
      }
    });
  }
}
